import * as Sentry from "@sentry/react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { ClerkProvider, SignIn, useAuth } from "@clerk/react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import CookieBanner from "@/components/CookieBanner";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import TaxInfo from "@/pages/TaxInfo";
import DonorDashboard from "@/pages/DonorDashboard";
import DebtorDashboard from "@/pages/DebtorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import FilingCabinet from "@/pages/FilingCabinet";
import PayoutBatches from "@/pages/admin/PayoutBatches";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import TaxCenter from "@/pages/admin/TaxCenter";
import Form990 from "@/pages/admin/Form990";
import AdminJobsQueue from "@/pages/admin/AdminJobsQueue";
import ComplianceAlerts from "@/pages/admin/ComplianceAlerts";
import PaymentDashboard from "@/pages/admin/PaymentDashboard";
import BoardGovernanceDashboard from "@/pages/admin/BoardGovernanceDashboard";
import SecretaryDashboard from "@/pages/admin/SecretaryDashboard";
import TreasurerDashboard from "@/pages/admin/TreasurerDashboard";
import BoardGateway from "@/pages/BoardGateway";
import CreditorPortal from "@/pages/CreditorPortal";
import CreditorPortalVerify from "@/pages/CreditorPortalVerify";
import RoleSelection from "@/pages/RoleSelection";
import IdentityCollection from "@/pages/IdentityCollection";
import SignUpGateway from "@/pages/SignUpGateway";
import AdminGateway from "@/pages/AdminGateway";
import About from "@/pages/About";
import Unsubscribed from "@/pages/Unsubscribed";
import Leaderboard from "@/pages/Leaderboard";
import ImpactPage from "@/pages/ImpactPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import PoolsPage from "@/pages/PoolsPage";
import JoinPage from "@/pages/JoinPage";
import { getMe, verifyAdminSession, verifyBoardSession } from "@workspace/api-client-react";
import { Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import "@/lib/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 30,
    },
  },
});

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * Redirects authenticated admins away from /select-role.
 * Rendered globally inside AppRoutes so it fires on every navigation.
 * Uses useEffect to avoid setState-during-render warnings.
 */
function AdminRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const [location, navigate] = useLocation();

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: isLoaded && !!isSignedIn,
  });

  const shouldRedirect = !!(me?.isAdmin && location === "/select-role");

  useEffect(() => {
    if (shouldRedirect) navigate("/admin");
  }, [shouldRedirect, navigate]);

  return null;
}

/**
 * When a signed-in user with a confirmed role lands on a public marketing page,
 * redirect them straight to their dashboard. They don't need to see the website —
 * their dashboard IS their home.
 */
const PUBLIC_ROUTES_THAT_REDIRECT = new Set([
  "/", "/donate", "/how-it-works", "/pools", "/join", "/about", "/leaderboard", "/impact",
]);

function AuthenticatedRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const [location, navigate] = useLocation();

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: isLoaded && !!isSignedIn,
  });

  // Strictly require roleConfirmedAt — matching the backend requireRole middleware.
  // Legacy accounts (role set but roleConfirmedAt null) will be redirected to
  // /select-role where they can confirm their existing role in one click.
  const isConfirmed = !!me?.roleConfirmedAt;
  const onPublicRoute = PUBLIC_ROUTES_THAT_REDIRECT.has(location);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !me) return;
    if (me.isAdmin) {
      if (onPublicRoute || location === "/select-role") navigate("/admin");
      return;
    }
    if (!isConfirmed) {
      // Signed in but role not yet confirmed — guide to role selection from any
      // public marketing page so they can confirm / pick their role.
      if (onPublicRoute) navigate("/select-role");
      return;
    }
    if (!onPublicRoute) return;
    if (me.role === "donor") navigate("/donor");
    else if (me.role === "debtor") navigate("/debtor");
  }, [isLoaded, isSignedIn, me, isConfirmed, onPublicRoute, location, navigate]);

  return null;
}

type MeBoard = { isBoard?: boolean; isAdmin?: boolean };

/**
 * Wraps admin pages that require full admin access.
 * - Requires the admin session cookie (gateway key) — that is the sole credential.
 * - No Clerk sign-in required; the gateway key alone grants access.
 */
function AdminProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const [, navigate] = useLocation();

  const { data: sessionData, isLoading } = useQuery({
    queryKey: ["adminSession"],
    queryFn: verifyAdminSession,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isLoading) return;
    if (!sessionData?.valid) navigate("/admin-entry");
  }, [isLoading, sessionData, navigate]);

  if (isLoading || !sessionData?.valid) return <LoadingScreen />;
  return <Component />;
}

/**
 * Wraps the board governance portal.
 * - Requires the admin session cookie (gateway password)
 * - Requires Clerk sign-in (isAdmin or isBoard on the signed-in account)
 * - Cookie-only sessions (no Clerk identity) are redirected to /admin-entry
 */
function BoardProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const [, navigate] = useLocation();
  const { isSignedIn, isLoaded } = useAuth();

  const { data: sessionData, isLoading: sessionLoading } = useQuery({
    queryKey: ["adminSession"],
    queryFn: verifyAdminSession,
    retry: false,
    staleTime: 60_000,
  });

  const { data: me, isLoading: meLoading, isError: meError } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: !!sessionData?.valid && isLoaded && !!isSignedIn,
    retry: false,
    staleTime: 60_000,
  });

  const clerkLoading = !isLoaded;
  const isLoading = sessionLoading || clerkLoading || (!!sessionData?.valid && !!isSignedIn && meLoading);
  const meTyped = me as MeBoard | undefined;

  const noSession = !isLoading && !sessionData?.valid;
  const notSignedIn = !isLoading && isLoaded && !isSignedIn;
  const noAccess = !isLoading && isLoaded && !!isSignedIn && !meLoading && (meError || (!!me && !meTyped?.isAdmin && !meTyped?.isBoard));

  useEffect(() => {
    if (isLoading) return;
    if (noSession || notSignedIn || noAccess) navigate("/admin-entry");
  }, [isLoading, noSession, notSignedIn, noAccess, navigate]);

  if (isLoading || noSession || notSignedIn || noAccess) return <LoadingScreen />;

  return <Component />;
}

/**
 * Protects board officer routes (no Clerk required).
 * Verifies the board_session cookie and optionally checks the role.
 */
function BoardOfficerProtectedRoute({
  component: Component,
  requiredRole,
}: {
  component: React.ComponentType;
  requiredRole: "secretary" | "treasurer";
}) {
  const [, navigate] = useLocation();

  const { data: sessionData, isLoading } = useQuery({
    queryKey: ["boardSession"],
    queryFn: verifyBoardSession,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isLoading) return;
    if (!sessionData?.valid) { navigate("/board-entry"); return; }
    if (sessionData.boardRole !== requiredRole) { navigate("/board-entry"); return; }
  }, [isLoading, sessionData, requiredRole, navigate]);

  if (isLoading || !sessionData?.valid || sessionData.boardRole !== requiredRole) {
    return <LoadingScreen />;
  }

  return <Component />;
}

type MeWithIdentity = {
  identityCollectedAt?: string | null;
};

function ProtectedRoute({
  component: Component,
  requiredRole,
}: {
  component: React.ComponentType;
  requiredRole?: "donor" | "debtor" | "admin";
}) {
  const { isSignedIn, isLoaded } = useAuth();
  const [location, navigate] = useLocation();

  // isAdmin routes are delegated to AdminProtectedRoute which has its own auth
  // logic (gateway cookie check). We still need to call all hooks first so that
  // the hook count stays the same on every render — required by React's Rules
  // of Hooks. The early return for admin must come AFTER all hook calls.
  const isAdmin = requiredRole === "admin";

  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: !isAdmin && isLoaded && !!isSignedIn,
  });

  const isStillLoading = !isAdmin && (!isLoaded || (!!isSignedIn && meLoading));

  // Compute redirect target purely from current state (no side effects during render).
  // Skipped for admin routes since AdminProtectedRoute handles its own redirects.
  let redirectTarget: string | null = null;
  if (!isAdmin && !isStillLoading) {
    if (!isSignedIn) {
      redirectTarget = "/sign-in";
    } else if (me) {
      const userIsAdmin = me.isAdmin === true;
      if (userIsAdmin) {
        redirectTarget = "/admin";
      } else {
        // Strictly require roleConfirmedAt — matching the backend requireRole
        // middleware.  Legacy accounts with me.role but no me.roleConfirmedAt
        // are redirected to /select-role to confirm their existing role in one
        // click; the backend will also backfill roleConfirmedAt on first access.
        const isConfirmed = !!me.roleConfirmedAt;

        if (!isConfirmed) {
          if (requiredRole) redirectTarget = "/select-role";
        } else {
          if (!requiredRole) {
            if (me.role === "donor") redirectTarget = "/donor";
            else if (me.role === "debtor") redirectTarget = "/debtor";
          } else if (me.role !== requiredRole) {
            if (me.role === "donor") redirectTarget = "/donor";
            else if (me.role === "debtor") redirectTarget = "/debtor";
            else redirectTarget = "/";
          } else if (requiredRole === "debtor" && location !== "/identity") {
            const meId = me as typeof me & MeWithIdentity;
            if (!meId.identityCollectedAt) redirectTarget = "/identity";
          }
        }
      }
    }
  }

  // useEffect must be called unconditionally — after all state/derived values,
  // before any early returns — so hooks are always called in the same order.
  useEffect(() => {
    if (redirectTarget) navigate(redirectTarget);
  }, [redirectTarget, navigate]);

  // Now safe to early-return for admin: all hooks above have already been called.
  if (isAdmin) return <AdminProtectedRoute component={Component} />;

  if (isStillLoading || redirectTarget) return <LoadingScreen />;

  return <Component />;
}

function PageTransition({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.14, ease: "easeOut" }}
      style={{ willChange: "opacity" }}
    >
      {children}
    </motion.div>
  );
}

function AppRoutes() {
  const [location] = useLocation();
  return (
    <>
    <AdminRedirect />
    <AuthenticatedRedirect />
    <AnimatePresence mode="wait" initial={false}>
    <PageTransition key={location}>
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/donate" component={Landing} />
      <Route path="/impact" component={ImpactPage} />
      <Route path="/how-it-works" component={HowItWorksPage} />
      <Route path="/pools" component={PoolsPage} />
      <Route path="/join" component={JoinPage} />
      <Route path="/admin-entry" component={AdminGateway} />
      <Route path="/about" component={About} />
      <Route path="/leaderboard" component={Leaderboard} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/tax-info" component={TaxInfo} />
      <Route path="/unsubscribed" component={Unsubscribed} />
      <Route path="/sign-in">
        <div className="min-h-screen flex flex-col bg-background">
          <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
            <div className="max-w-4xl mx-auto px-4 h-14 flex items-center">
              <a
                href={BASE || "/"}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </a>
            </div>
          </nav>
          <div className="flex-1 flex items-center justify-center">
            <SignIn
              routing="path"
              path={`${BASE}/sign-in`}
              signUpUrl={`${BASE}/sign-up`}
              fallbackRedirectUrl={BASE || "/"}
            />
          </div>
        </div>
      </Route>
      <Route path="/sign-in/:rest*">
        <div className="min-h-screen flex flex-col bg-background">
          <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
            <div className="max-w-4xl mx-auto px-4 h-14 flex items-center">
              <a
                href={BASE || "/"}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </a>
            </div>
          </nav>
          <div className="flex-1 flex items-center justify-center">
            <SignIn
              routing="path"
              path={`${BASE}/sign-in`}
              signUpUrl={`${BASE}/sign-up`}
              fallbackRedirectUrl={BASE || "/"}
            />
          </div>
        </div>
      </Route>
      <Route path="/sign-up">
        <SignUpGateway />
      </Route>
      <Route path="/sign-up/:rest*">
        <SignUpGateway />
      </Route>
      <Route path="/select-role">
        <ProtectedRoute component={RoleSelection} />
      </Route>
      <Route path="/identity">
        <ProtectedRoute component={IdentityCollection} requiredRole="debtor" />
      </Route>
      <Route path="/donor">
        <ProtectedRoute component={DonorDashboard} requiredRole="donor" />
      </Route>
      <Route path="/debtor">
        <ProtectedRoute component={DebtorDashboard} requiredRole="debtor" />
      </Route>
      <Route path="/admin">
        <ProtectedRoute component={AdminDashboard} requiredRole="admin" />
      </Route>
      <Route path="/admin/filing-cabinet">
        <ProtectedRoute component={FilingCabinet} requiredRole="admin" />
      </Route>
      <Route path="/admin/payout-batches">
        <ProtectedRoute component={PayoutBatches} requiredRole="admin" />
      </Route>
      <Route path="/admin/analytics">
        <ProtectedRoute component={AdminAnalytics} requiredRole="admin" />
      </Route>
      <Route path="/admin/tax-center">
        <ProtectedRoute component={TaxCenter} requiredRole="admin" />
      </Route>
      <Route path="/admin/form-990">
        <ProtectedRoute component={Form990} requiredRole="admin" />
      </Route>
      <Route path="/admin/jobs">
        <ProtectedRoute component={AdminJobsQueue} requiredRole="admin" />
      </Route>
      <Route path="/admin/compliance">
        <ProtectedRoute component={ComplianceAlerts} requiredRole="admin" />
      </Route>
      <Route path="/admin/payment-dashboard">
        <ProtectedRoute component={PaymentDashboard} requiredRole="admin" />
      </Route>
      <Route path="/admin/board">
        <BoardProtectedRoute component={BoardGovernanceDashboard} />
      </Route>
      <Route path="/board-entry" component={BoardGateway} />
      <Route path="/admin/secretary">
        <BoardOfficerProtectedRoute component={SecretaryDashboard} requiredRole="secretary" />
      </Route>
      <Route path="/admin/treasurer">
        <BoardOfficerProtectedRoute component={TreasurerDashboard} requiredRole="treasurer" />
      </Route>
      <Route path="/creditor-portal" component={CreditorPortal} />
      <Route path="/creditor-portal/verify" component={CreditorPortalVerify} />
      <Route component={NotFound} />
    </Switch>
    </PageTransition>
    </AnimatePresence>
    </>
  );
}

function ErrorFallback({ error }: { error?: unknown }) {
  // Log the real error so it appears in production browser console / server logs
  // even before Sentry DSN is configured.
  if (error) {
    console.error("[ErrorBoundary] Unhandled render error:", error);
  }
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center bg-background">
      <AlertTriangle className="h-12 w-12 text-destructive" />
      <h1 className="text-2xl font-semibold">Something went wrong</h1>
      <p className="text-muted-foreground max-w-md">
        An unexpected error occurred. Our team has been notified. Please refresh the page or go home to try again.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
        >
          Refresh page
        </button>
        <button
          onClick={() => { window.location.href = "/"; }}
          className="px-4 py-2 rounded-md border border-border bg-background text-foreground text-sm font-medium hover:bg-muted"
        >
          Go to home page
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={(props) => <ErrorFallback error={props.error} />}>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/select-role"
        proxyUrl={
          typeof window !== "undefined" && window.location.hostname !== "localhost"
            ? `${window.location.origin}/api/__clerk`
            : undefined
        }
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <WouterRouter base={BASE}>
              <AppRoutes />
            </WouterRouter>
            <Toaster />
            <CookieBanner />
          </TooltipProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
